import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, PieChart, Pie, Legend,
} from "recharts";
import {
  Warning, Calendar, ChartPie, Stack, Ruler, House,
  Buildings, Wallet, Coins, TrendUp, Clock, CurrencyDollar,
} from "@phosphor-icons/react";
import Brand from "@/components/Brand";
import { formatCurrency } from "@/lib/format";
import {
  MetaItem, KpiBlock, SpendToDateCard, GrossRevenueCard,
  ProfitProjectionCard, RevenueTableBody, VendorTableBody, MoneyTooltip,
} from "@/components/project-summary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PIE_COLORS = ["#327de3", "#0E7036", "#F59E0B", "#D92D20", "#7C3AED", "#0891B2", "#DB2777", "#65A30D", "#EA580C", "#0E4B92", "#14B8A6", "#A21CAF"];

export default function SharePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/share/${token}`)
      .then(({ data }) => { if (!cancelled) setShare(data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8] text-sm text-slate-500">Loading…</div>;
  }

  if (error || !share) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8]">
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center max-w-md">
          <Warning size={28} className="text-amber-500 mx-auto" />
          <h2 className="mt-3 font-display text-xl font-bold text-slate-900">Link unavailable</h2>
          <p className="mt-2 text-sm text-slate-500">{error || "This share link is invalid or has expired."}</p>
        </div>
      </div>
    );
  }

  const data = share.project_summary;
  const sectionSet = new Set(share.sections || []);
  const project = data?.project || {};
  const kpis = data?.kpis || {};
  const items = data?.items || [];
  const revenueItems = data?.revenue_items || [];
  const categories = (data?.categories || []).filter((c) => (c.name || "").toLowerCase() !== "revenue");
  const expenseItems = items.filter((it) => (it.class_type || "").trim().toLowerCase() !== "revenue");
  const vendor = share.vendor_summary;

  const showSec = (key) => sectionSet.size === 0 || sectionSet.has(key);

  const dataAgeText = share.mode === "live"
    ? "Data is live · auto-updates with each refresh of the source"
    : `Data is static · based on last update on ${share.captured_at ? new Date(share.captured_at).toLocaleString() : "—"}`;

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      {/* Header */}
      <header className="bg-[#327de3] text-white h-16 flex items-center px-4 sm:px-6 lg:px-8">
        <Brand size="md" color="navy" asLink={false} />
        <span className="ml-auto text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] bg-white/10 rounded-full px-3 py-1">
          Read-only share
        </span>
      </header>

      {/* Status sub-header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Project Summary</div>
            <div className="font-display text-sm sm:text-base font-semibold text-slate-900 truncate">{share.business_name}</div>
          </div>
          <div className="text-[10px] sm:text-[11px] font-semibold text-[#D92D20] text-right" data-testid="share-data-status">{dataAgeText}</div>
        </div>
      </div>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-3">
        {!data ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-sm text-slate-500 text-center">No data captured for this link.</div>
        ) : (
          <>
            {showSec("summary") && (
              <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
                  <MetaItem icon={<Calendar size={16} weight="bold" />} label="Start date" value={project.start_date || "—"} />
                  <MetaItem icon={<Calendar size={16} weight="bold" />} label="Projected end date" value={project.projected_end_date || "—"} />
                  <MetaItem icon={<Clock size={16} weight="bold" />} label="Duration" value={project.duration_years ? `${project.duration_years.toFixed(2)} yrs` : "—"} />
                  <MetaItem icon={<House size={16} weight="bold" />} label="Total units" value={project.unit_count ? project.unit_count.toLocaleString() : "—"} />
                  <MetaItem icon={<Ruler size={16} weight="bold" />} label="Total square feet" value={project.square_feet ? `${project.square_feet.toLocaleString()} SF` : "—"} />
                  <MetaItem icon={<Stack size={16} weight="bold" />} label="Avg SF / unit" value={project.avg_sf_per_unit ? `${Math.round(project.avg_sf_per_unit).toLocaleString()} SF` : "—"} />
                </div>
              </section>
            )}

            {showSec("kpis") && (
              <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <SpendToDateCard metric={kpis.spend_to_date} project={project} />
                <KpiBlock label="Total Remaining to Spend" sublabel="(Budget Minus Paid)" metric={kpis.total_remaining} accent="#7C3AED" icon={<Coins size={18} weight="bold" />} />
                <KpiBlock label="Total Budgeted" metric={kpis.total_budgeted} accent="#1f64c2" icon={<CurrencyDollar size={18} weight="bold" />} />
              </section>
            )}

            {showSec("gross-revenue") && (
              <GrossRevenueCard metric={kpis.gross_proceeds} project={project} onClick={() => {}} />
            )}

            {showSec("profit") && (
              <ProfitProjectionCard
                spendToDate={kpis.spend_to_date}
                totalBudgeted={kpis.total_budgeted}
                grossProceeds={kpis.gross_proceeds}
                project={project}
              />
            )}

            {showSec("spend") && (
              <ShareCard title="Spend by Category" icon={<ChartPie size={16} weight="bold" />}>
                {categories.filter((c) => Math.abs(c.total) > 0).length === 0 ? (
                  <div className="text-sm text-slate-400 text-center py-6">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={340}>
                    <PieChart>
                      <Pie
                        data={categories.filter((c) => Math.abs(c.total) > 0)}
                        dataKey="total" nameKey="name"
                        cx="50%" cy="50%" outerRadius={95} innerRadius={55} paddingAngle={2}
                        labelLine={{ stroke: "#94a3b8", strokeWidth: 1 }}
                        label={(entry) => `${(entry.percent * 100).toFixed(1)}%`}
                        isAnimationActive={false}
                      >
                        {categories.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<MoneyTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ShareCard>
            )}

            {showSec("comparison") && (
              <ShareCard title="Budget vs Spent by Category" icon={<Buildings size={16} weight="bold" />}>
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={categories} margin={{ left: 0, right: 8, top: 16, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5E6A78" }} angle={-25} textAnchor="end" height={70} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: "#5E6A78" }} tickFormatter={(v) => formatCurrency(v, { compact: true })} />
                    <Tooltip content={<MoneyTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="budget" name="Budget" fill="#CBD5E1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="total" name="Spent" fill="#327de3" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ShareCard>
            )}

            {showSec("expenses") && (
              <ShareCard title="Expenses" icon={<Wallet size={16} weight="bold" />} noPadding>
                <ExpensesReadOnly items={expenseItems} />
              </ShareCard>
            )}

            {showSec("vendor") && vendor && (
              <ShareCard title="Expenses by Vendor" icon={<Buildings size={16} weight="bold" />} noPadding>
                <VendorTableBody data={vendor} loading={false} error="" onRetry={() => {}} />
              </ShareCard>
            )}

            {showSec("revenue") && (
              <ShareCard title="Revenue" icon={<TrendUp size={16} weight="bold" />} noPadding>
                <RevenueTableBody items={revenueItems} />
              </ShareCard>
            )}
          </>
        )}

        <div className="text-center text-xs text-slate-400 pt-4">
          <Link to="/login" className="hover:text-slate-600">Open Strata</Link>
        </div>
      </main>
    </div>
  );
}

function ShareCard({ title, icon, children, noPadding = false }) {
  return (
    <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-[#327de3]/10 text-[#327de3] flex items-center justify-center">{icon}</div>
        <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
      </div>
      <div className={noPadding ? "" : "p-6"}>{children}</div>
    </section>
  );
}

function ExpensesReadOnly({ items }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-y border-slate-200">
          <tr>
            <th className="px-5 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
            <th className="px-5 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Class Type</th>
            <th className="px-5 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Budget</th>
            <th className="px-5 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Paid</th>
            <th className="px-5 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Payables</th>
            <th className="px-5 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={6} className="px-5 py-6 text-center text-sm text-slate-400">No expense items.</td></tr>
          ) : items.map((it, idx) => (
            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
              <td className="px-5 py-[5px] text-slate-800 font-medium">{it.name}</td>
              <td className="px-5 py-[5px] text-slate-500 text-xs hidden md:table-cell">{it.class_type}</td>
              <td className="px-5 py-[5px] text-right tabular-nums text-slate-700">{formatCurrency(it.budget)}</td>
              <td className="px-5 py-[5px] text-right tabular-nums text-slate-900 font-medium">{formatCurrency(it.paid)}</td>
              <td className="px-5 py-[5px] text-right tabular-nums text-slate-700">{formatCurrency(it.unpaid)}</td>
              <td className="px-5 py-[5px] text-right tabular-nums text-slate-900 font-semibold">{formatCurrency(it.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
